import React from "react";
import PropTypes from "prop-types";
import { Panel } from "../Panel";
import cn from "./style.module.scss";

/**
 * Tabs.Tabs
 *  - root component of Tabs
 *    accepts generic props for Tabs and Handles businessLogic
 */
export function Tabs(props) {
  const [tab, setTab] = React.useState(props.default);

  React.useEffect(() => {
    setTab(props.default);
  }, [props.default]);

  const setOnMenuClick = (menu) => {
    if (menu.props.onClick) return menu.props.onClick;
    else return setTab;
  };

  const getMenuClass = (menu) => {
    if (menu.props.value === tab)
      return `${cn.menu} ${cn.active} ${menu.props.className}--selected`;
    else return `${cn.menu} ${menu.props.className}`;
  };

  const cloneMenu = (menu) =>
    React.cloneElement(menu, {
      children: menu.props.children,
      value: menu.props.value,
      className: getMenuClass(menu),
      onClick: setOnMenuClick(menu),
    });

  const cloneMenuItems = (menuList) => {
    if (Array.isArray(menuList)) return menuList.map(cloneMenu);
    else return cloneMenu(menuList);
  };

  const getMenuListClass = (list) => `${cn.tab_list} ${list.props.className}`;

  const cloneMenuList = (component) => {
    if (component) {
      return React.cloneElement(component, {
        className: getMenuListClass(component),
        children: cloneMenuItems(component.props.children),
      });
    } else return null;
  };

  const renderTabList = () => cloneMenuList(props.children[0]);

  const getContentClassName = (content) =>
    `${cn.tab_content} ${content.props.className}`;

  const renderContent = () => {
    const content = props.children.reduce(
      (selected, content) => {
        if (content.props?.menuValue === tab) {
          selected = content;
        }
        return selected;
      },
      <Panel menuValue="EMPTY">
        <p>No Content</p>
      </Panel>
    );

    return React.cloneElement(content, {
      children: content.props.children,
      className: getContentClassName(content),
    });
  };

  const className = `${cn.tab} ${props.className}`;

  return (
    <div className={className}>
      {renderTabList()}
      {renderContent()}
    </div>
  );
}

Tabs.propTypes = {
  /**
   * ClassName
   */
  className: PropTypes.string,
  /**
   * List of Children components
   */
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element])
    .isRequired,
  /**
   * menuValue for the tab that needs to be selected by default
   */
  default: PropTypes.string.isRequired,
};

Tabs.defaultProps = {
  className: "",
  default: "",
};
